import moment from 'moment';
import 'moment-duration-format';
import getAjaxUrl from '../route/ajax-url';
import { UnitSystem } from '../utils/number-formatter';
import PackageBarsCalculator from './package-bars';
import h from '../../libs/jsx';
import initMinigallery from './package-minigallery';

const url = 'ajax/packagepricing.cfm';
const action = 'packagepricing';

const packageWrapperSelector = '.height-package';
const statsOutSelectorSumBrutto = '.price-calculation-result.sum-brutto';
const statsOutSelectorSumNetto = '.price-calculation-result.sum-netto';
const statsOutSelectorAreaBrutto = '.price-calculation-result.area-brutto';
const statsOutSelectorAreaNetto = '.price-calculation-result.area-netto';
const statsOutSelectorSurfaceNetto = '.price-calculation-result.surface-netto';
const statsOutSelectorSurfaceBrutto = '.price-calculation-result.surface-brutto';
const statsOutSelectorStructureNetto = '.price-calculation-result.structure-netto';
const statsOutSelectorStructureBrutto = '.price-calculation-result.structure-brutto';
const statsOutSelectorPadPositions = '.price-calculation-result.pad-positions';
const statsOutputSelectorProcessTime = '.price-calculation-result.process-time';
const statsOutputSelectorWeight = '.price-calculation-result.weight';
const statsOutputSelectorInstallationSteps = '.price-calculation-result.installation-steps';
const statsOutputSelectorBars = '.price-calculation-result.bars';
const comparisonOutputBars = '.package-compare .comparison-bars';
const comparisonOutputBarsOutput = '.comparison-bars-output';
const galleryWrapper = '.package-minigallery.package-minigallery-with-svg';
const galleryOutput = '.package-minigallery.package-minigallery-with-svg > div';

const DEFAULT_PACKAGE_BATCH_SIZE = 1;
const DEFAULT_PACKAGE_CONCURRENT_REQUESTS = 5;

export const allPackagesCalculatedEvent = 'tp-all-packages-calculated';
export default class PackageCalculator {
  constructor(options = {}) {
    ({
      timeFormat: this.timeFormat = 'h',
      timeUnit: this.timeUnit,
      packageIds: this.packageIds = [],
      visiblePackageIds: this.visiblePackageIds = [],
      batchSize: this.batchSize = DEFAULT_PACKAGE_BATCH_SIZE,
      concurrentRequests: this.concurrentRequests = DEFAULT_PACKAGE_CONCURRENT_REQUESTS,
    } = options);

    this.results = {};
    this.calculatedCount = 0;
    this.unitSystem = window.unitSystem === 'imperial' ? UnitSystem.IMPERIAL : UnitSystem.METRIC;
  }

  fetchAll() {
    const packageIds = this.prioritizeVisiblePackages(this.packageIds, this.visiblePackageIds);
    this.calculatePackages(packageIds);
  }

  calculatePackages(packageIds) {
    // Base case: no more packages to calculate
    if (packageIds.length === 0) {
      return;
    }

    // Calculate the first batchSize * concurrentRequests packages
    const currentPackageIds = packageIds.slice(0, this.batchSize * this.concurrentRequests);

    // The rest of the packages will be calculated in the next iteration
    const rest = packageIds.slice(this.batchSize * this.concurrentRequests);

    // We want to wait for all the packages to be calculated before moving on
    Promise.allSettled(
      // Each request will calculate batchSize packages
      this.group(currentPackageIds, this.batchSize)
        .map(this.calculatePackagesBatch.bind(this)))
      .then(() => {
        // Calculate the rest of the packages
        this.calculatePackages(rest);
      });
  }

  group(array, size) {
    let result = [];

    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }

    return result;
  }

  calculatePackagesBatch(packageIds) {
    return this.getPackagePricesBatch(packageIds)
      .then(this.onBatchPackagesCalculated.bind(this))
      .catch(console.error);
  }

  prioritizeVisiblePackages(packageIds, visiblePackageIds) {
    const visibleSet = new Set(visiblePackageIds);

    const invisiblePackageIds = packageIds.filter((packageId) => !visibleSet.has(packageId));

    return visiblePackageIds.concat(invisiblePackageIds);
  }

  onBatchPackagesCalculated(xhrResponse) {
    try {
      const response = JSON.parse(xhrResponse);

      for (const packageId in response) {
        if (!response[packageId].error) {
          this.results[packageId] = response[packageId];
          this.fillResults(packageId, response[packageId]);
        } else {
          this.fillError(packageId);
        }

        this.calculatedCount += 1;
      }

      this.triggerEventIfAllCalculated();
    } catch (e) {
      console.error(e);
    }
  }

  getPackagePricesBatch(packageIds) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();

      //use getAjaxUrl to obtain url in case cookies are deactivated
      xhr.open('POST', getAjaxUrl(url));
      formData.append('action', action);

      for (const packageId of packageIds) {
        formData.append('package_id', packageId);
      }

      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve(xhr.response);
        } else {
          reject(xhr.statusText);
        }
      };

      xhr.onerror = () => {
        reject(xhr.statusText);
      };

      xhr.send(formData);
    });
  }

  triggerEventIfAllCalculated() {
    if (this.calculatedCount === this.packageIds.length) {
      this.populateBarIndicators();
      const event = new CustomEvent(allPackagesCalculatedEvent, { detail: { calculations: this.results } });
      document.dispatchEvent(event);
    }
  }

  getOutput(result, renderedValue) {
    switch (renderedValue) {
      case 'price_sum_netto':
        return result.localised.price_sum_netto;
      case 'price_sum_brutto':
        return result.localised.price_sum_brutto;
      case 'price_surface_netto':
        return result.localised.price_surface_netto;
      case 'price_surface_brutto':
        return result.localised.price_surface_brutto;
      case 'price_structure_netto':
        return result.localised.price_structure_netto;
      case 'price_structure_brutto':
        return result.localised.price_structure_brutto;
      case 'price_sum_brutto':
        return result.localised.price_sum_brutto;
      case 'price_m2_netto':
        return result.localised.price_m2_netto;
      case 'price_m2_brutto':
        return result.localised.price_m2_brutto;
      case 'price_ft2_netto':
        return result.localised.price_ft2_netto;
      case 'price_ft2_brutto':
        return result.localised.price_ft2_brutto;
      case 'pad_positions':
        return result.plan_pad_positions_count;
      case 'process_time': {
        const seconds = result['process_time'];
        const duration = moment.duration(seconds, 'seconds');
        return `${duration.format(this.timeFormat, { trim: false })}&nbsp;${this.timeUnit}`;
      }
      case 'weight':
        return result.weight + '&nbsp;kg';
      default:
        if (result[renderedValue] !== undefined) {
          return result[renderedValue];
        } else {
          return '';
        }
    }
  }

  fillResults(packageId, result) {
    var parent = document.querySelector(packageWrapperSelector + '[data-package-id="' + packageId + '"]');
    var outputSumBrutto = parent.querySelector(statsOutSelectorSumBrutto);
    var outputSumNetto = parent.querySelector(statsOutSelectorSumNetto);
    var outputSurfaceBrutto = parent.querySelector(statsOutSelectorSurfaceBrutto);
    var outputSurfaceNetto = parent.querySelector(statsOutSelectorSurfaceNetto);
    var outputStructureBrutto = parent.querySelector(statsOutSelectorStructureBrutto);
    var outputStructureNetto = parent.querySelector(statsOutSelectorStructureNetto);
    var outputAreaNetto = parent.querySelector(statsOutSelectorAreaNetto);
    var outputAreaBrutto = parent.querySelector(statsOutSelectorAreaBrutto);
    var padPositions = parent.querySelector(statsOutSelectorPadPositions);
    var installationSteps = parent.querySelector(statsOutputSelectorInstallationSteps);
    var processTime = parent.querySelector(statsOutputSelectorProcessTime);
    var weight = parent.querySelector(statsOutputSelectorWeight);

    parent.dataset.totalPriceNet = result.price_sum_netto;
    parent.dataset.totalPriceNetFormatted = this.getOutput(result, 'price_sum_netto');
    parent.dataset.totalPriceGross = result.price_sum_brutto;
    parent.dataset.totalPriceGrossFormatted = this.getOutput(result, 'price_sum_brutto');

    if (outputSumBrutto !== null) {
      outputSumBrutto.innerHTML = this.getOutput(result, 'price_sum_brutto');
    }
    if (outputSumNetto !== null) {
      outputSumNetto.innerHTML = this.getOutput(result, 'price_sum_netto');
    }
    if (outputSurfaceBrutto !== null) {
      outputSurfaceBrutto.innerHTML = this.getOutput(result, 'price_surface_brutto');
    }
    if (outputSurfaceNetto !== null) {
      outputSurfaceNetto.innerHTML = this.getOutput(result, 'price_surface_netto');
    }
    if (outputStructureBrutto !== null) {
      outputStructureBrutto.innerHTML = this.getOutput(result, 'price_structure_brutto');
    }
    if (outputStructureNetto !== null) {
      outputStructureNetto.innerHTML = this.getOutput(result, 'price_structure_netto');
    }
    if (outputAreaNetto !== null) {
      outputAreaNetto.innerHTML = this.getOutput(
        result,
        this.unitSystem === UnitSystem.IMPERIAL ? 'price_ft2_netto' : 'price_m2_netto'
      );
    }
    if (outputAreaBrutto !== null) {
      outputAreaBrutto.innerHTML = this.getOutput(
        result,
        this.unitSystem === UnitSystem.IMPERIAL ? 'price_ft2_brutto' : 'price_m2_brutto'
      );
    }
    if (padPositions !== null) {
      padPositions.innerHTML = this.getOutput(result, 'pad_positions');
    }
    if (installationSteps !== null) {
      installationSteps.innerHTML = this.getOutput(result, 'installation_steps');
    }
    if (processTime !== null) {
      processTime.innerHTML = this.getOutput(result, 'process_time');
    }
    if (weight !== null) {
      weight.innerHTML = this.getOutput(result, 'weight');
    }

    if (parent.querySelector(galleryOutput) && window.galleries?.[packageId] && result.ukplan_combined_svg) {
      const slider = window.galleries[packageId];
      slider.destroy();
      const newGalleryItem = (
        <div dangerouslySetInnerHTML={{ __html: result.ukplan_combined_svg }}>
          <div class="tiny-slider-label">{tp_trans.trans('package_svg_label', 'package_svg_label')}</div>
        </div>
      );
      this.recalculateSVGViewbox(newGalleryItem.querySelector('svg'));
      parent.querySelector(galleryOutput).prepend(newGalleryItem);

      initMinigallery(parent.querySelectorAll(galleryWrapper));
    }
  }

  /**
  * Transforms the viewBox coordinates to maintain a 3:2 aspect ratio,
  * ensuring that the original graphics is centered and not cropped.
  *
  * @param {Element} item - SVG DOM element
  */
  recalculateSVGViewbox(item) {
    const currentViewBox = item.getAttribute('viewBox');
    const viewBoxValues = currentViewBox.split(' ').map(parseFloat);
    const [x, y, width, height] = viewBoxValues;
    const originalViewBox = { x: x, y: y, width: width, height: height };
    const originalWidth = originalViewBox.width;
    const originalHeight = originalViewBox.height;
    const targetAspectRatio = 3 / 2;

    let targetWidth = originalWidth;
    let targetHeight = originalHeight;
    if (originalWidth / originalHeight > targetAspectRatio) {
      targetHeight = originalWidth / targetAspectRatio;
    } else {
      targetWidth = originalHeight * targetAspectRatio;
    }

    const newX = originalViewBox.x + (originalWidth - targetWidth) / 2;
    const newY = originalViewBox.y + (originalHeight - targetHeight) / 2;

    const newViewBox = {
      x: newX,
      y: newY,
      width: targetWidth,
      height: targetHeight
    };

    // Update the SVG element with the new viewBox
    item.setAttribute(
      'viewBox',
      `${newViewBox.x} ${newViewBox.y} ${newViewBox.width} ${newViewBox.height}`
    );
  }

  fillError(packageId) {
    var parent = document.querySelector(packageWrapperSelector + '[data-package-id="' + packageId + '"]');
    for (const output of parent.querySelectorAll('.price-loader')) {
      output.parentElement.innerHTML = '-';
    }
  }

  populateBarIndicators() {
    const barsCalculator = new PackageBarsCalculator({ calculations: this.results });
    for (const packageId of this.packageIds) {
      const parent = document.querySelector(packageWrapperSelector + '[data-package-id="' + packageId + '"]');
      const outputElement = parent.querySelector(statsOutputSelectorBars);
      if (!outputElement) {
        continue;
      }
      const config = outputElement.dataset.config;
      const renderLabels = outputElement.dataset.renderLabels;
      const renderBarsAsText = outputElement.dataset.renderBarsAsText;
      const barHTML = barsCalculator.createBarHTML(packageId, config, renderLabels === 'true', renderBarsAsText === 'true');
      outputElement.innerHTML = '';
      outputElement.append(barHTML);
    }
  }

  populateBarIndicatorsInCompareModal() {
    const barsCalculator = new PackageBarsCalculator({ calculations: this.results });
    for (const packageId of this.packageIds) {
      const parent = document.querySelector(comparisonOutputBars + '[data-package-id="' + packageId + '"]');
      const outputElement = parent?.querySelector(comparisonOutputBarsOutput);
      if (!outputElement) {
        continue;
      }
      const barHTML = barsCalculator.createBarAllHTML(packageId);
      outputElement.innerHTML = '';
      outputElement.append(barHTML);
    }
  }

}
